import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2, Mail } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function AdminLogin() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (sessionStorage.getItem("floxora_admin_token")) navigate("/admind/panel");
  }, [navigate]);

  const saveAndGo = (token) => {
    sessionStorage.setItem("floxora_admin_token", token);
    navigate("/admind/panel");
  };

  const handleOtpRequest = async (e) => {
    e.preventDefault();
    if (!email) {
      toast.error("Enter your email first");
      return;
    }
    setLoading(true);
    try {
      await api.post("/auth/otp/request", { email });
      setOtpSent(true);
      toast.success("OTP sent to your email");
    } catch {
      toast.error("Failed to send OTP");
    } finally {
      setLoading(false);
    }
  };

  const handleOtpVerify = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/auth/otp/verify", { email, otp });
      saveAndGo(data.access_token);
    } catch (err) {
      toast.error(err.response?.data?.detail || "OTP verify failed");
    } finally {
      setLoading(false);
    }
  };

  const inputCls = "bg-black/40 border-white/15 h-11 rounded-xl focus-visible:ring-[#A78BFA]";

  return (
    <div className="min-h-screen bg-[#050505] text-white flex items-center justify-center px-6" data-testid="admin-login-page">
      <motion.div
        initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-sm bg-[#0a0a0a] border border-white/10 rounded-2xl p-8"
      >
        <span className="font-['Outfit'] font-extrabold text-2xl">Floxora<span className="text-[#A78BFA]">.</span></span>
        <p className="text-zinc-500 text-sm mt-1 mb-6 flex items-center gap-2">
          <Mail className="w-4 h-4 text-[#A78BFA]" /> Admin Panel — OTP Login
        </p>

        <form onSubmit={otpSent ? handleOtpVerify : handleOtpRequest} className="space-y-5" data-testid="otp-login-form">
          <div className="space-y-2">
            <Label htmlFor="admin-email" className="text-zinc-300">Email</Label>
            <Input
              id="admin-email" type="email" data-testid="admin-email-input" placeholder="floxora.work@gmail.com"
              value={email} onChange={(e) => setEmail(e.target.value)}
              className={inputCls} required
            />
          </div>
          {otpSent && (
            <div className="space-y-2">
              <Label htmlFor="otp-input" className="text-zinc-300">6-Digit OTP</Label>
              <Input
                id="otp-input" data-testid="otp-input" maxLength={6} placeholder="••••••"
                value={otp} onChange={(e) => setOtp(e.target.value.replace(/\D/g, ""))}
                className={`${inputCls} text-center tracking-[0.5em] font-bold text-lg`} required
              />
            </div>
          )}
          <button
            type="submit" disabled={loading} data-testid="otp-submit-button"
            className="w-full rounded-full bg-[#A78BFA] text-black font-bold py-3 hover:opacity-90 transition-opacity active:scale-95 disabled:opacity-60 flex items-center justify-center gap-2"
          >
            {loading && <Loader2 className="w-4 h-4 animate-spin" />}
            {otpSent ? "Verify & Login" : "Send OTP"}
          </button>
          {otpSent && (
            <p className="text-center text-xs text-zinc-500">
              Didn't receive the OTP?{" "}
              <button type="button" onClick={handleOtpRequest} className="text-[#A78BFA]" data-testid="otp-resend-button">Resend</button>
            </p>
          )}
        </form>
      </motion.div>
    </div>
  );
}
